/**
 * 模組頁面代碼 : 1321-UI-05
 * 頁面說明 : 梯控申請新增修改視窗
 * @module web/modules/elevator-apply/components/ElevatorApplyModal
 */

// ----- import from npm modules -----
import React, { useState, useEffect } from 'react';

// ----- constants -----
const APPLY_NEW = 'new';
const APPLY_EDIT = 'edit';

const RESPONSE_OK = 200;
const TITLE_APPLY_NEW = '新增梯控申請資料';
const TITLE_APPLY_EDIT = '修改梯控申請資料';

const ElevatorApplyModal = React.forwardRef((props, ref) => {
    const { mode, applDetail, buildingSelection, searchElevatorApply, reloadApplyModal } = props;
    let modalIcon = mode === APPLY_NEW ? '#ico-plus' : '#ico-edit';
    let modalTitleText = mode === APPLY_NEW ? TITLE_APPLY_NEW : TITLE_APPLY_EDIT;

    // 初始化state
    const [formValues, setFormValues] = useState({
        modalApplyBuilding: '',
        modalApplyElevator: '',
        modalApplicantName: '',
        modalApplicantMobile: '',
        modalApplicantType: '',
        modalApplicantHouseNum: '',
        modalApplicantFloor: '',
        modalApplicantRoomNum: '',
        modalApplyFloor: '',
        modalValidateTime: new Date().toISOString().split('T')[0],
        modalApplyTime: '',
        modalPermitTime: '',
        modalApplyStatus: 'P',
    });

    const [elevatorList, setElevatorList] = useState([]);
    const [applyFloorList, setApplyFloorList] = useState([]);
    const [invalidShow, setInvalidShow] = useState('none');

    // 更新電梯選項
    const updateElevatorList = (buildingId) => {
        const building = buildingSelection.find(building => building.buildingId === buildingId);
        setElevatorList(building?.elevators || []);
    };

    // 更新樓層選項
    const updateApplyFloorList = (buildingId, elevatorId) => {
        const building = buildingSelection.find(building => building.buildingId === buildingId);
        const newElevatorId = elevatorId ? elevatorId : building?.elevators.length > 0 ? building.elevators[0].elevatorId : '';
        const elevator = building?.elevators.find(elevator => elevator.elevatorId === newElevatorId);
        setApplyFloorList(elevator?.floors || []);
    };

    // 日期欄位顥示格轉換 : "yyyy-MM-ddThh:mm"
    const formatDateTimeLocal = (date) => {
        const pad = (num) => (num < 10 ? '0' + num : num);
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    // Handle change event
    const handleChange = (event) => {
        const target = event.target;
        let value = target.value;

        if (target.type === 'radio') {
            value = target.id.slice(-1);
        }

        const applicantType = formValues.modalApplicantType;
        let newValidateDate = null;

        // 若為住戶，有效期限為永久
        if (target.name === 'modalApplyBuilding') {
            const buildingId = parseInt(value);
            updateElevatorList(buildingId);
            updateApplyFloorList(buildingId, null);
            const expirationDay = buildingSelection.find(building => building.buildingId === buildingId).expirationDay;
            const currentDate = new Date();
            currentDate.setDate(currentDate.getDate() + expirationDay);
            newValidateDate = applicantType === 'R' ? new Date('2910-12-31T23:59:59') : currentDate;
        }

        if (target.name === 'modalApplicantType' && value === 'R') {
            const currentDate = new Date('2910-12-31T23:59:59');
            newValidateDate = currentDate;
        }

        if (target.name === 'modalApplyElevator') {
            const buildingId = parseInt(formValues.modalApplyBuilding);
            const elevatorId = parseInt(value);
            updateApplyFloorList(buildingId, elevatorId);
        }

        if (target.name !== 'modalValidateTime' && newValidateDate !== null) {
            setFormValues({
                ...formValues,
                [target.name]: value,
                modalValidateTime: formatDateTimeLocal(newValidateDate),
            });
        } else {
            setFormValues({
                ...formValues,
                [target.name]: value,
            });
        }

    };


    // 使用useEffect在applDetail改變才更新formValues
    useEffect(() => {
        if (reloadApplyModal) {
            setFormValues({
                modalApplyBuilding: applDetail !== null ? applDetail.buildingId : '',
                modalApplyElevator: applDetail !== null ? applDetail.elevatorId : '',
                modalApplicantName: applDetail !== null ? applDetail.applicant.applicantName : '',
                modalApplicantMobile: applDetail !== null ? applDetail.applicant.mobile : '',
                modalApplicantType: applDetail !== null ? applDetail.applicant.applicantType : '',
                modalApplicantHouseNum: applDetail !== null ? applDetail.applicant.houseNum : '',
                modalApplicantFloor: applDetail !== null ? applDetail.applicant.houseFloorId : '',
                modalApplicantRoomNum: applDetail !== null ? applDetail.applicant.houseRoom : '',
                modalApplyFloor: applDetail !== null ? applDetail.applyFloor : '',
                modalValidateTime: applDetail !== null ? applDetail.validateTime : getMinDateTime(),
                modalApplyTime: applDetail !== null ? applDetail.applyTime : '',
                modalPermitTime: applDetail !== null ? applDetail.permitTime : '',
                modalApplyStatus: applDetail !== null ? applDetail.flgStatus : 'P',
            });

            if (applDetail !== null) {
                updateElevatorList(applDetail.buildingId);
                updateApplyFloorList(applDetail.buildingId, applDetail.elevatorId);
            } else {
                setElevatorList([]);
                setApplyFloorList([]);
            }

            setInvalidShow('none');
            props.setReloadApplyModal(false);
        }
    }, [applDetail, reloadApplyModal]);

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            setInvalidShow('block');
            form.classList.add('was-validated');
            return;
        }
        //form.classList.add('was-validated');

        // 建立 FormData 讀取表单的值
        const formData = new FormData(form);

        // 建立住戶物件
        const saveApplDetail = {
            applDetailId: mode === APPLY_NEW ? null : applDetail.applDetailId,
            applicantId: mode === APPLY_NEW ? null : applDetail.applicant.applicantId,
            buildingId: parseInt(formData.get('modalApplyBuilding')),
            elevatorId: parseInt(formData.get('modalApplyElevator')),
            applicantName: formData.get('modalApplicantName'),
            mobile: formData.get('modalApplicantMobile'),
            applicantType: formData.get('modalApplicantType'),
            applyFloor: parseInt(formData.get('modalApplyFloor')),
            validateTime: formData.get('modalValidateTime'),
            flgStatus: formData.get('modalApplyStatus'),
        };

        // 呼叫父層元件的新增或修改梯控申請
        if (mode === APPLY_NEW) {
            props.addApply(saveApplDetail).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_APPLY_NEW, response.payload);
                } else {
                    // 调用 fetchApplyList and Hide the modal
                    const { buildingId, elevatorId, applicantName, applicantType } = searchElevatorApply;
                    props.fetchApplyList(buildingId, elevatorId, applicantName, applicantType).then(() => {
                        props.setNeedElevatorApplyLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        } else if (mode === APPLY_EDIT) {
            props.editApply(saveApplDetail).then((response) => {
                const statusCode = response.payload.code;
                if (statusCode !== RESPONSE_OK) {
                    $(ref.current).modal('hide');
                    props.onErrorHandle(TITLE_APPLY_EDIT, response.payload);
                } else {
                    // 调用 fetchApplyList and Hide the modal
                    const { buildingId, elevatorId, applicantName, applicantType } = searchElevatorApply;
                    props.fetchApplyList(buildingId, elevatorId, applicantName, applicantType).then(() => {
                        props.setNeedElevatorApplyLoad(true);
                        $(ref.current).modal('hide');
                    });
                }
            });
        }
    };

    // Function to get the min valid date and time in the required format
    const getMinDateTime = () => {
        const now = new Date();
        now.setHours(now.getHours() + 1);
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    return (
        <div ref={ref}
            className="modal fade"
            id="modalApply"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="modalApplyLabel"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <form className="modal__form modal-content needs-validation" id="modalApplyForm" onSubmit={handleSubmit}>
                    <div className="modal-header">
                        <h1 className="modal-title fs-5" id="modalApplyLabel">
                            <div className="modal__title__badge">
                                <svg className="badge__icon" aria-hidden="true">
                                    <use href={modalIcon}></use>
                                </svg>
                            </div>
                            <div className="modal__title__text">{modalTitleText}</div>
                        </h1>
                        <button type="button" className="btn-close btn-cancel" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div className="modal-body">
                        <div className="d-flex flex-column gap-3 px-2">
                            {mode === APPLY_EDIT &&
                                <div className="modal__form__catagory row" id="modalApplyCatagoryStatus">
                                    <div className="col-3">
                                        <div className="modal__form__catagory__lable">申請狀態</div>
                                    </div>
                                    <div className="col-9">
                                        <div className="row">
                                            <div className="col-12">
                                                <label htmlFor="modalApplyTime" className="form-label">申請時間</label>
                                                <input
                                                    type="datetime-local"
                                                    className="form-control form-control-sm"
                                                    id="modalApplyTime"
                                                    value={formValues.modalApplyTime}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                        <div className="row">
                                            <div className="col-6">
                                                <label htmlFor="modalApplyStatus" className="form-label">狀態</label>
                                                <select
                                                    className="form-select form-select-sm"
                                                    id="modalApplyStatus"
                                                    name="modalApplyStatus"
                                                    value={formValues.modalApplyStatus}
                                                    onChange={handleChange}
                                                >
                                                    <option value="P">待核可</option>
                                                    <option value="A">有效</option>
                                                    <option value="I">無效</option>
                                                    <option value="B">黑名單</option>
                                                </select>
                                            </div>
                                            <div className="col-6">
                                                <label htmlFor="modalPermitTime" className="form-label">核可時間</label>
                                                <input
                                                    type="datetime-local"
                                                    className="form-control form-control-sm"
                                                    id="modalPermitTime"
                                                    value={formValues.modalPermitTime}
                                                    disabled
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">申請人資料</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantName" className="form-label">申請人姓名*</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalApplicantName"
                                                name="modalApplicantName"
                                                value={formValues.modalApplicantName}
                                                onChange={handleChange}
                                                required />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantMobile" className="form-label">手機號碼*</label>
                                            <input
                                                type="tel"
                                                pattern="09[0-9]{8}"
                                                className="form-control form-control-sm"
                                                id="modalApplicantMobile"
                                                name="modalApplicantMobile"
                                                value={formValues.modalApplicantMobile}
                                                onChange={handleChange}
                                                required
                                            />
                                            <div className="invalid-feedback">請輸入正確格式</div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantType" className="form-label">身份*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplicantType"
                                                name="modalApplicantType"
                                                value={formValues.modalApplicantType}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                <option value="R">住戶</option>
                                                <option value="V">訪客</option>
                                                <option value="E">職員</option>
                                                <option value="B">業主</option>
                                                <option value="O">其他</option>
                                            </select>
                                        </div>
                                        {/*
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantHouseNum" className="form-label">戶號</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalApplicantHouseNum"
                                                name="modalApplicantHouseNum"
                                                value={formValues.modalApplicantHouseNum}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        */}
                                    </div>
                                    {/*
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantFloor" className="form-label">樓層</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplicantFloor"
                                                name="modalApplicantFloor"
                                                value={formValues.modalApplicantFloor}
                                                onChange={handleChange}
                                            >
                                                <option disabled value="">請選擇</option>
                                                <option>...</option>
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalApplicantRoomNum" className="form-label">室號</label>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                id="modalApplicantRoomNum"
                                                name="modalApplicantRoomNum"
                                                value={formValues.modalApplicantRoomNum}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    */}
                                </div>
                            </div>
                            <div className="modal__form__catagory row">
                                <div className="col-3">
                                    <div className="modal__form__catagory__lable">申請內容</div>
                                </div>
                                <div className="col-9">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplyBuilding" className="form-label">大樓名稱*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyBuilding"
                                                name="modalApplyBuilding"
                                                value={formValues.modalApplyBuilding}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                {buildingSelection.map((building, index) => (
                                                    <option key={index} value={building.buildingId}>{building.buildingName}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalApplyElevator" className="form-label">電梯編號*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyElevator"
                                                name="modalApplyElevator"
                                                value={formValues.modalApplyElevator}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                {elevatorList && elevatorList.map((elevator, index) => (
                                                    <option key={index} value={elevator.elevatorId}>{elevator.elevatorName}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="modalApplyFloor" className="form-label">申請樓層*</label>
                                            <select
                                                className="form-select form-select-sm"
                                                id="modalApplyFloor"
                                                name="modalApplyFloor"
                                                value={formValues.modalApplyFloor}
                                                onChange={handleChange}
                                                required>
                                                <option disabled value="">請選擇</option>
                                                {applyFloorList && applyFloorList.map((floor, index) => (
                                                    <option key={index} value={floor.floorNum}>{floor.floorDisplay}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="modalValidateTime" className="form-label">有效期限*</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control form-control-sm"
                                                id="modalValidateTime"
                                                name="modalValidateTime"
                                                value={formValues.modalValidateTime}
                                                onChange={handleChange}
                                                //min={getMinDateTime()} // Set the min attribute to the current date and time
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <div className="alert alert-warning global-invalid-feedback" style={{ display: invalidShow }}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">取消</button>
                        <button type="submit" className="btn btn-primary">儲存</button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default ElevatorApplyModal;